import React from 'react';
import Card from '../shared/Card';
import StatusBadge from '../shared/StatusBadge';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import useVitals from '../../hooks/useVitals';
import { useAuth } from '../../lib/auth';

const { FiHeart, FiZap, FiActivity, FiWifi, FiWifiOff } = FiIcons;

// Fallback values shown before any live data arrives
const FALLBACK_VITALS = { hr: null, stress: null, stability: null };

const VitalsModule = ({ data }) => {
  const { user } = useAuth();

  // Wire to live socket events — merges over fallback as events arrive.
  // `data` prop (if passed) is used as the initial fallback so existing
  // call sites that pass static data still work without change.
  const { vitals, isLive, lastUpdated, source } = useVitals(
    user?.id,
    data ?? FALLBACK_VITALS
  );

  const display = {
    hr:        vitals?.hr        ?? data?.hr        ?? '--',
    stress:    vitals?.stress    ?? data?.stress    ?? '--',
    stability: vitals?.stability ?? data?.stability ?? '--',
  };

  const vitalCards = [
    { id: 'hr',        label: 'Heart Rate',    value: display.hr,        unit: 'BPM',   icon: FiHeart,    color: 'text-rose-500'  },
    { id: 'stress',    label: 'Neural Stress', value: display.stress,    unit: 'Index', icon: FiZap,      color: 'text-amber-500' },
    { id: 'stability', label: 'PFC Stability', value: display.stability, unit: '%',     icon: FiActivity, color: 'text-brand-500' },
  ];

  return (
    <div className="space-y-3">
      {/* Live indicator */}
      <div className="flex items-center justify-between px-1">
        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
          Vitals Monitor
        </p>
        <div className="flex items-center gap-1.5">
          <SafeIcon
            icon={isLive ? FiWifi : FiWifiOff}
            className={`text-xs ${isLive ? 'text-emerald-400' : 'text-slate-600'}`}
          />
          <span className={`text-[10px] font-bold uppercase tracking-widest ${isLive ? 'text-emerald-400' : 'text-slate-600'}`}>
            {isLive
              ? `Live · ${lastUpdated?.toLocaleTimeString()}`
              : 'Awaiting signal'}
          </span>
        </div>
      </div>

      {/* Vital cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {vitalCards.map((vital) => (
          <Card key={vital.id} className="p-8 group hover:border-brand-200 transition-colors">
            <div className="flex justify-between items-start mb-6">
              <div className={cn("w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center transition-transform group-hover:scale-110", vital.color)}>
                <SafeIcon icon={vital.icon} className="text-2xl" />
              </div>
              <StatusBadge status={parseInt(vital.value) > 100 ? 'high' : 'normal'} />
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
              {vital.label}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-black text-gray-900">{vital.value}</span>
              <span className="text-xs font-bold text-gray-400 uppercase">{vital.unit}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VitalsModule;